import React, { useMemo, useState } from 'react';
import { clipTopicName } from '../../utils/topicUtils';

/**
 * Lightweight markdown-to-HTML for LLM responses.
 * Handles: **bold**, *italic*, `code`, ```blocks```, - lists, numbered lists, headers, line breaks.
 */
function renderMarkdown(text) {
  if (!text) return '';

  // Escape HTML entities
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Fenced code blocks (```...```)
  html = html.replace(/```[\s\S]*?```/g, (match) => {
    const code = match.slice(3, -3).replace(/^\w*\n/, ''); // strip optional language tag
    return `<pre><code>${code}</code></pre>`;
  });

  // Inline code (`...`)
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Bold (**...**  or __...__)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic (*...* or _..._) — avoid matching list markers
  html = html.replace(/(?<!\w)\*([^*]+?)\*(?!\w)/g, '<em>$1</em>');
  html = html.replace(/(?<!\w)_([^_]+?)_(?!\w)/g, '<em>$1</em>');

  // Headers (### ... , ## ... , # ...)
  html = html.replace(/^### (.+)$/gm, '<strong>$1</strong>');
  html = html.replace(/^## (.+)$/gm, '<strong>$1</strong>');
  html = html.replace(/^# (.+)$/gm, '<strong>$1</strong>');

  // Unordered list items (- or *)
  html = html.replace(/^[\-\*] (.+)$/gm, '<li>$1</li>');

  // Numbered list items (1. 2. etc)
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');

  // Line breaks (double newline = paragraph break, single = <br>)
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br/>');

  // Wrap in paragraph
  html = `<p>${html}</p>`;

  // Clean up empty paragraphs
  html = html.replace(/<p>\s*<\/p>/g, '');

  return html;
}

const ENGINE_LABELS = {
  rag:              { label: 'From documents',    cls: 'engine-rag' },
  context:          { label: 'From this view',    cls: 'engine-context' },
  context_fallback: { label: 'From this view (documents unavailable)', cls: 'engine-fallback' },
  both:             { label: 'Documents + this view', cls: 'engine-both' },
};

// Turn a topic-model label like "25_radionet_infraia-_orp_radio" into the same
// short display name the dashboard shows (e.g. "Radionet Infraia").
function cleanTopicLabel(label) {
  if (!label) return '';
  return clipTopicName(String(label)) || '';
}

// Build a human-friendly citation label from the source metadata.
// Prefers the project acronym + framework programme; falls back to project id,
// then logbook title, then the raw filename.
function citationLabel(s, locator) {
  const acronym = s.project_acronym && String(s.project_acronym).trim();
  const programme = s.framework_programme && String(s.framework_programme).trim();
  if (acronym) {
    return programme ? `${acronym} · ${programme}` : acronym;
  }
  if (s.project_id) return `Project ${s.project_id}`;
  return s.logbook_title || locator.file || (s.chunk_id ? s.chunk_id.slice(0, 12) : 'Source');
}

// Per-claim trust display: symbol + label + css class.
// Mirrors LISA's verification chips (verified / unverified / not in source).
const TRUST_META = {
  verified:   { sym: '✓', label: 'verified',   cls: 'trust-verified' },
  unverified: { sym: '~', label: 'unverified', cls: 'trust-unverified' },
  not_found:  { sym: '✗', label: 'not in source', cls: 'trust-notfound' },
};

/**
 * Individual chat message bubble.
 */
export default function ChatMessage({ message, onOpenDocument, onRerun, onFollowUp, onFeedback, onRetry, isLast }) {
  const { role, content, engine, sources, claims, coverage, classification, rewrittenQuery, query, followUps, answerParts } = message;
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [claimsOpen, setClaimsOpen] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  // Feedback state: null | 'up' | 'down' (sent), 'commenting' = down pending comment
  const [feedbackSent, setFeedbackSent] = useState(null);
  const [commenting, setCommenting] = useState(false);
  const [comment, setComment] = useState('');
  // chunk_ids of the currently-focused claim — highlights matching sources.
  const [activeChunkIds, setActiveChunkIds] = useState(null);

  const renderedContent = useMemo(() => {
    if (role === 'assistant') {
      return renderMarkdown(content);
    }
    return null;
  }, [role, content]);

  // "Both" engine answers arrive as structured halves — render as sub-cards.
  const renderedParts = useMemo(() => {
    if (role === 'assistant' && Array.isArray(answerParts) && answerParts.length > 0) {
      return answerParts.map(p => ({ ...p, html: renderMarkdown(p.text) }));
    }
    return null;
  }, [role, answerParts]);

  const engineInfo = ENGINE_LABELS[engine];
  const hasSources = Array.isArray(sources) && sources.length > 0;
  const hasClaims = Array.isArray(claims) && claims.length > 0;
  const hasCoverage = coverage && coverage.total_claims != null;
  const routeReason = classification && classification.reason;
  // Engines the user can manually re-run as (exclude the current one).
  const rerunTargets = (onRerun && query)
    ? [
        { route: 'rag', label: '📄 Documents' },
        { route: 'context', label: '📊 Analytics' },
        { route: 'both', label: 'Both' },
      ].filter(t => t.route !== engine)
    : [];

  // Toggle the claim-focus: clicking a claim highlights its sources and opens the panel.
  const focusClaim = (chunkIds) => {
    setActiveChunkIds(prev => {
      const next = (prev && chunkIds && prev.join() === chunkIds.join()) ? null : (chunkIds || null);
      if (next && next.length) setSourcesOpen(true);
      return next;
    });
  };

  return (
    <div className={`chat-message ${role}`}>
      {role === 'assistant' && engineInfo && (
        <div className={`chat-engine-label ${engineInfo.cls}`}>
          {engine === 'rag' || engine === 'both' ? '📄' : '📊'} {engineInfo.label}
          {(routeReason || rerunTargets.length > 0) && (
            <button
              className="chat-why-toggle"
              onClick={() => setWhyOpen(o => !o)}
              title="Why this engine? Re-run on another engine."
            >why{whyOpen ? ' ▾' : ' ▸'}</button>
          )}
        </div>
      )}
      {role === 'assistant' && whyOpen && (routeReason || rerunTargets.length > 0) && (
        <div className="chat-route-why">
          {routeReason && (
            <div className="chat-route-reason">
              <span className="chat-route-reason-label">Routing:</span> {routeReason}
            </div>
          )}
          {rewrittenQuery && rewrittenQuery !== query && (
            <div className="chat-route-searched">
              <span className="chat-route-reason-label">Searched for:</span> “{rewrittenQuery}”
            </div>
          )}
          {rerunTargets.length > 0 && (
            <div className="chat-rerun-row">
              <span className="chat-route-reason-label">Re-run as:</span>
              {rerunTargets.map(t => (
                <button
                  key={t.route}
                  className="chat-rerun-btn"
                  onClick={() => { setWhyOpen(false); onRerun(t.route); }}
                >{t.label}</button>
              ))}
            </div>
          )}
        </div>
      )}
      {role === 'assistant' ? (
        renderedParts ? (
          <div className="chat-answer-parts">
            {renderedParts.map((p, i) => (
              <div key={i} className="chat-answer-part">
                <div className="chat-answer-part-label">
                  {p.engine === 'rag' ? '📄' : '📊'} {p.label}
                </div>
                <div className="chat-message-md" dangerouslySetInnerHTML={{ __html: p.html }} />
              </div>
            ))}
          </div>
        ) : (
          <div className="chat-message-md" dangerouslySetInnerHTML={{ __html: renderedContent }} />
        )
      ) : (
        <div>{content}</div>
      )}
      {role === 'assistant' && message.error && onRetry && query && (
        <button className="chat-retry-btn" onClick={onRetry}>↻ Retry</button>
      )}
      {((hasCoverage && (coverage.total_claims > 0 || coverage.total_sources > 0)) || hasSources) && (
        <div className="chat-msg-footer">
          {hasCoverage && coverage.total_claims > 0 && (
            <button
              className="chat-footer-chip"
              onClick={() => hasClaims && setClaimsOpen(o => !o)}
              disabled={!hasClaims}
              title={coverage.transparency || ''}
            >
              <span className="chat-verify-check">✓</span>
              {coverage.verified_claims}/{coverage.total_claims} verified
              {coverage.unverified_claims > 0 && (
                <span className="chat-verify-warn"> · {coverage.unverified_claims} unverified</span>
              )}
              {coverage.not_found_claims > 0 && (
                <span className="chat-verify-bad"> · {coverage.not_found_claims} unsupported</span>
              )}
              {hasClaims && <span className="chat-verify-caret">{claimsOpen ? '▾' : '▸'}</span>}
            </button>
          )}
          {hasSources && (
            <button className="chat-footer-chip" onClick={() => setSourcesOpen(o => !o)}>
              📄 {sources.length} source{sources.length !== 1 ? 's' : ''}
              <span className="chat-verify-caret">{sourcesOpen ? '▾' : '▸'}</span>
            </button>
          )}
        </div>
      )}
      {claimsOpen && hasClaims && (
        <div className="chat-verification">
          {coverage && coverage.transparency && (
            <div className="chat-verify-transparency">{coverage.transparency}</div>
          )}
          <ul className="chat-claims-list">
            {claims.map((cl, i) => {
              const t = TRUST_META[cl.trust] || TRUST_META.unverified;
              const cids = Array.isArray(cl.chunk_ids) ? cl.chunk_ids : [];
              const isActive = activeChunkIds && cids.length > 0 && activeChunkIds.join() === cids.join();
              return (
                <li
                  key={i}
                  className={`chat-claim-item ${t.cls}${cids.length ? ' chat-claim-clickable' : ''}${isActive ? ' chat-claim-active' : ''}`}
                  onClick={() => cids.length && focusClaim(cids)}
                  title={cids.length ? 'Show the source(s) backing this claim' : ''}
                >
                  <span className="chat-claim-sym" title={t.label}>{t.sym}</span>
                  <span className="chat-claim-text">{cl.text}</span>
                  {cl.n_sources > 0 && (
                    <span className="chat-claim-count">{cl.n_sources} src</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {hasSources && sourcesOpen && (
        <div className="chat-sources">
          {activeChunkIds && (
            <button className="chat-sources-clearfocus" onClick={() => setActiveChunkIds(null)}>
              Showing sources for selected claim · show all
            </button>
          )}
          <ul className="chat-sources-list">
              {sources.map((s, i) => {
                const locator = s.source_locator || {};
                const label = citationLabel(s, locator);
                const topic = cleanTopicLabel(s.doc_topic_label);
                const url = s.source_url || locator.source_url;
                const excerpt = (s.text || '').slice(0, 180).replace(/\s+/g, ' ').trim();
                const matchesFocus = activeChunkIds ? activeChunkIds.includes(s.chunk_id) : null;
                const itemCls = matchesFocus === null
                  ? 'chat-source-item'
                  : `chat-source-item ${matchesFocus ? 'chat-source-focus' : 'chat-source-dim'}`;
                return (
                  <li key={s.chunk_id || i} className={itemCls}>
                    <span className="chat-source-file">
                      {onOpenDocument && locator.file ? (
                        <button
                          className="chat-source-open"
                          onClick={() => onOpenDocument({ filename: locator.file, paraIdx: locator.para_idx })}
                          title={locator.file ? `Open ${locator.file} in document viewer` : 'Open in document viewer'}
                        >
                          {label}
                        </button>
                      ) : url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer" title={locator.file || ''}>{label}</a>
                      ) : (
                        <span title={locator.file || ''}>{label}</span>
                      )}
                      {locator.para_idx != null && (
                        <span className="chat-source-para"> ¶{locator.para_idx + 1}</span>
                      )}
                      {url && (
                        <a
                          className="chat-source-cordis"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View project on CORDIS"
                        >↗</a>
                      )}
                    </span>
                    {topic && <span className="chat-source-topic">{topic}</span>}
                    {excerpt && <span className="chat-source-excerpt">"{excerpt}{s.text?.length > 180 ? '…' : ''}"</span>}
                  </li>
                );
              })}
          </ul>
        </div>
      )}
      {role === 'assistant' && isLast && onFollowUp
        && Array.isArray(followUps) && followUps.length > 0 && (
        <div className="chat-followups">
          <div className="chat-followups-label">Follow up:</div>
          {followUps.map((fu, i) => (
            <button key={i} className="chat-followup-chip" onClick={() => onFollowUp(fu.q)}>
              <span className="chat-followup-icon">{fu.mode === 'data' ? '📊' : '📄'}</span>{fu.q}
            </button>
          ))}
        </div>
      )}
      {role === 'assistant' && onFeedback && engine && (
        <div className="chat-feedback">
          {feedbackSent ? (
            <span className="chat-feedback-thanks">
              {feedbackSent === 'up' ? 'Thanks for the feedback!' : 'Thanks — noted.'}
            </span>
          ) : commenting ? (
            <div className="chat-feedback-comment">
              <input
                type="text"
                className="chat-feedback-input"
                placeholder="What was wrong? (optional)"
                value={comment}
                maxLength={500}
                autoFocus
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onFeedback('down', comment);
                    setFeedbackSent('down');
                  } else if (e.key === 'Escape') {
                    setCommenting(false);
                  }
                }}
              />
              <button
                className="chat-feedback-submit"
                onClick={() => { onFeedback('down', comment); setFeedbackSent('down'); }}
              >Send</button>
            </div>
          ) : (
            <>
              <button
                className="chat-feedback-btn"
                title="Helpful answer"
                onClick={() => { onFeedback('up', ''); setFeedbackSent('up'); }}
              >👍</button>
              <button
                className="chat-feedback-btn"
                title="Not helpful"
                onClick={() => setCommenting(true)}
              >👎</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

